import { getPasswordValidators } from '@abp/ng.theme.shared';
import { Component, EventEmitter, inject, Injector, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { ClientService, CreateClientUserDto } from '@proxy/clients';
import { LocalizationService } from '@abp/ng.core';
import { DOCUMENT } from '@angular/common';
import { environment } from '../../../../environments/environment';
import { SelectedSommelierService } from '../../services/selected-sommelier.service';
import { map, Observable, tap } from 'rxjs';
import { NotificationService } from '../../services/notification.service';
interface Client {
  name: string;
  email: string;
  phone: string;
  address: string;
}

interface ExtendedCreateClientUserDto extends CreateClientUserDto {
  password: string;
}

@Component({
  selector: 'app-client-register-form',
  templateUrl: './client-register-form.component.html',
  standalone: false
})
export class ClientRegisterFormComponent implements OnInit, OnChanges {
  @Input() slug!: string;
  @Output() clientSubmitted = new EventEmitter<Client>();
  @Output() cancelled = new EventEmitter<void>();
  @Output() saved = new EventEmitter<Client>();
  form!: FormGroup;

  pickups$: Observable<Array<{ value: number, label: string }>> = new Observable();
  private firstPickupId: number | null = null;
  private readonly injector = inject(Injector);
  private readonly doc = inject(DOCUMENT) as Document;
  constructor(private fb: FormBuilder,
     private clientService: ClientService,
     private l: LocalizationService,
     private slugService: SelectedSommelierService,
     private notificationService: NotificationService
  ) {}

 ngOnInit(): void {
  this.buildForm();

  this.pickups$ = this.clientService.getPickUpsBySommelier(this.slug).pipe(
    map(pickups => pickups.map(pickup => ({
      value: pickup.id,
      label: `${pickup.name} - ${pickup.address}`
    }))),
    tap(pickupOptions => {
      if (pickupOptions.length > 0) {
        this.firstPickupId = pickupOptions[0].value;
        this.form.get('pickUpId')?.setValue(this.firstPickupId);
      }
    })
  );
}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['slug'] && !changes['slug'].firstChange) {
      this.form.get('slug')?.setValue(this.slug);
    }
  }

  private buildForm(): void {
    this.form = this.fb.group({
      slug: [this.slug || '', Validators.required],
      name: ['', [Validators.required]],
      surname: ['', [Validators.required]],
      email: ['', [Validators.required, Validators.email]],
      confirmEmail: ['', [Validators.required, Validators.email]],
      phoneNumber: ['', [Validators.required]],
      pickUpId:[''],
      documentNumber:['', [Validators.required]],
      address: [''],
      extraField: [''],
      password: ['', [
        Validators.required,
        ...getPasswordValidators(this.injector),
      ]],
      confirmPassword: ['', [Validators.required]]
    }, {
      validators: [this.passwordMatchValidator(), this.emailMatchValidator()]
    });
  }

  private passwordMatchValidator(): ValidatorFn {
    return (form: AbstractControl): ValidationErrors | null => {
      const password = form.get('password');
      const confirmPassword = form.get('confirmPassword');

      if (password?.value !== confirmPassword?.value) {
        confirmPassword?.setErrors({ passwordMismatch: true });
        return { passwordMismatch: true };
      } else {
        confirmPassword?.setErrors(null);
        return null;
      }
    };
  }

  private emailMatchValidator(): ValidatorFn {
    return (form: AbstractControl): ValidationErrors | null => {
      const email = form.get('email');
      const confirmEmail = form.get('confirmEmail');

      if (!email?.value || !confirmEmail?.value) {
        return null;
      }

      if (email.value !== confirmEmail.value) {
        const currentErrors = confirmEmail.errors || {};
        confirmEmail.setErrors({ ...currentErrors, emailMismatch: true });
        return { emailMismatch: true };
      } else {
        // Limpiar solo el error de emailMismatch, mantener otros errores
        if (confirmEmail.errors) {
          const { emailMismatch, ...rest } = confirmEmail.errors;
          confirmEmail.setErrors(Object.keys(rest).length ? rest : null);
        }
        return null;
      }
    };
  }

  onCancel(): void {
    this.cancelled.emit();
  }

  loginWithGoogle() {
    const api = environment.apis.default.url;
    const returnUrl = `${environment.application.baseUrl}/auth/google-callback`;
    const sommelierSlug = this.slug || '';

    let url = `${api}/external/register-google?returnUrl=${encodeURIComponent(returnUrl)}`;
    if (sommelierSlug) {
      url += `&sommelierSlug=${encodeURIComponent(sommelierSlug)}`;
    }

    this.doc.location.href = url;
  }


onSubmit() {
  if (this.form.get("extraField").value) {
    return;
  }

  // Asegurar que pickUpId tenga el primer valor si está vacío
  const pickUpIdControl = this.form.get('pickUpId');
  if (!pickUpIdControl?.value && this.firstPickupId !== null) {
    pickUpIdControl.setValue(this.firstPickupId);
  }

  if (this.form.invalid) {
    this.form.markAllAsTouched();
    return;
  }

  const { confirmPassword, confirmEmail, ...dtoData } = this.form.value;
  const dto = dtoData as ExtendedCreateClientUserDto;

  // El backend espera pickUpId como número o null — nunca '' (string vacío), porque no
  // deserializa contra long?/long y rompe el bindeo con un 400 (issue #333). Pasa cuando
  // el sommelier no tiene puntos de retiro cargados.
  if (!dto.pickUpId) {
    dto.pickUpId = null;
  }

  this.clientService.createClientUser(dto, { skipHandleError: true })
    .subscribe({
      next: (result) => {
        this.handleSuccess();
        this.notificationService.success(
          'Éxito',
          '::Client:CreatedAndCheckEmail'
        );
      },
      error: (error) => {
        const errorCode = error?.error?.error?.code;
        if (errorCode === 'ClientEmailAlreadyUsed') {
          this.notificationService.error(
            'Error',
            '::Client:EmailAlreadyUsed',
            [dto.email]
          );
        } else if (errorCode === 'DocumentNumberAlreadyExists') {
          this.notificationService.error(
            'Error',
            '::Client:DocumentNumberAlreadyExists',
            [dto.documentNumber]
          );
        } else {
          console.error('Error creando cliente:', error);
          this.notificationService.error(
            'Error',
            '::Client:ErrorCreating'
          );
        }
      }
    });
}

private handleSuccess() {
  this.saved.emit();
}

private passwordComplexityValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value;
    if (!value) {
      // si está vacío no evaluamos la regex
      control.setErrors(null);
      return null;
    }

    const regex = /^(?=.*[A-Z])(?=.*[^a-zA-Z0-9]).+$/;
    const isValid = regex.test(value);

    if (!isValid) {
      // si ya tiene otros errores, los conservamos
      const currentErrors = control.errors || {};
      currentErrors['passwordComplexity'] = true;
      control.setErrors(currentErrors);
      return { passwordComplexity: true };
    } else {
      // limpiar solo la clave passwordComplexity si estaba
      if (control.errors) {
        const { passwordComplexity, ...rest } = control.errors;
        control.setErrors(Object.keys(rest).length ? rest : null);
      }
      return null;
    }
  };
}

}
